import React, { useEffect, useState } from 'react';
import { fetchJson } from '../api';
import HeroSection from '../components/HeroSection';
import StateGrid from '../components/StateGrid';
import CitiesWithListings from '../components/CitiesWithListings';
import { generateWebsiteSchema, generateOrganizationSchema, generateSchemaGraph } from '../utils/schema';
import { generateCompleteSEO } from '../utils/seo';
import SEOHead from '../components/SEOHead';
import { getSiteBaseUrl } from '../config/site';

const HomeSchema = ({ states }) => {
  const baseUrl = getSiteBaseUrl();
  const schema = generateSchemaGraph([
    generateWebsiteSchema(),
    generateOrganizationSchema(),
    {
      '@type': 'WebPage',
      '@id': `${baseUrl}/#webpage`,
      url: baseUrl,
      name: 'Visibeen Directory - Find Local Businesses',
      description: 'Discover trusted local businesses, services and professionals across India',
      isPartOf: { '@id': `${baseUrl}/#website` },
      mainEntity: {
        '@type': 'ItemList',
        name: 'States in India',
        numberOfItems: states?.length || 0,
        itemListElement: (states || []).slice(0, 20).map((state, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: state.name || state.slug,
          url: `${baseUrl}/in/${state.slug}`
        }))
      }
    }
  ]);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

export default function Home() {
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [citiesWithListings, setCitiesWithListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [stateData, cityData, citiesWithListingsData] = await Promise.all([
          fetchJson('/api/v1/directory/states'),
          fetchJson('/api/v1/directory/cities'),
          fetchJson('/api/v1/directory/cities-with-listings')
        ]);
        setStates(stateData);
        setCities(cityData);
        setCitiesWithListings(citiesWithListingsData);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div>Loading states…</div>;
  if (error) return <div className="error">{error}</div>;

  const seoData = generateCompleteSEO({
    title: 'Find Local Businesses Across India',
    description: 'Discover trusted local businesses, services and professionals across India. Browse by state, city, and category. Find reviews, ratings, contact information, and more on Visibeen Directory.',
    keywords: [
      'local business India',
      'business directory',
      'find businesses',
      'local services',
      'business listings',
      'business reviews',
      'India business search',
      'local professionals'
    ],
    url: '/',
    type: 'website'
  });

  return (
    <div>
      <SEOHead
        title={seoData.title}
        description={seoData.meta.description}
        keywords={seoData.meta.keywords}
        canonical={seoData.link.canonical}
        robots={seoData.meta.robots}
        ogTitle={seoData.meta['og:title']}
        ogDescription={seoData.meta['og:description']}
        ogUrl={seoData.meta['og:url']}
        ogType={seoData.meta['og:type']}
        twitterTitle={seoData.meta['twitter:title']}
        twitterDescription={seoData.meta['twitter:description']}
      />

      <HomeSchema states={states} />
      <HeroSection states={states} cities={cities} />
      <StateGrid states={states} />
      <CitiesWithListings cities={citiesWithListings} />
    </div>
  );
}


